"use client";
import { useEffect, useState, useRef } from "react";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { HiMenu, HiX } from "react-icons/hi";
import { FaTruck, FaWarehouse, FaRoute, FaGlobe, FaInfoCircle, FaBriefcase, FaCompass, } from "react-icons/fa";


export default function Header() {
  const router = useRouter();
  const [menuOpen, setMenuOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [companyOpen, setCompanyOpen] = useState(false);
  const servicesRef = useRef(null);
  const companyRef = useRef(null);
  const mobileMenuRef = useRef(null);
  const mobileServicesRef = useRef(null);
  const mobileCompanyRef = useRef(null);

  // Handle scroll effect
  useEffect(() => {
    const onScroll = () => {
      if (window.innerWidth >= 1024) {
        setScrolled(window.scrollY > 20);
      } else {
        setScrolled(false);
      }
    };

    window.addEventListener("scroll", onScroll);
    window.addEventListener("resize", onScroll);
    onScroll(); // Initial check

    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, []);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      const isDesktop = window.innerWidth >= 1024;

      // Close desktop dropdowns only on desktop
      if (isDesktop) {
        if (
          servicesRef.current &&
          !servicesRef.current.contains(event.target)
        ) {
          setServicesOpen(false);
        }
        if (
          companyRef.current &&
          !companyRef.current.contains(event.target)
        ) {
          setCompanyOpen(false);
        }
      } else {
        // Close mobile dropdowns only on mobile
        if (
          mobileServicesRef.current &&
          !mobileServicesRef.current.contains(event.target)
        ) {
          setServicesOpen(false);
        }
        if (
          mobileCompanyRef.current &&
          !mobileCompanyRef.current.contains(event.target)
        ) {
          setCompanyOpen(false);
        }
      }

      // Close mobile menu when clicking outside
      if (
        mobileMenuRef.current &&
        !mobileMenuRef.current.contains(event.target)
      ) {
        const menuButton = event.target.closest("button");
        if (!menuButton || !menuButton.onclick) {
          setMenuOpen(false);
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Close mobile menu when route changes
  const closeMobileMenu = () => {
    setMenuOpen(false);
    setServicesOpen(false);
    setCompanyOpen(false);
  };

  const handleMobileNavClick = (href) => {
    router.push(href);
    closeMobileMenu();
  };



  return (
    <div className="top-0 left-0 w-full z-50">
      {/* Welcome Banner */}
      <div className="bg-gradient-to-r from-blue-900 via-blue-800 to-blue-900 hidden lg:flex items-center justify-center py-2.5 px-4 shadow-md">
        <div className="max-w-7xl mx-auto w-full flex items-center justify-center gap-4 text-white text-sm whitespace-nowrap">

          <div className="flex items-center gap-1">
            <span className="text-yellow-300 font-bold">For support:</span>
            <span className="font-semibold">+91 98710 37073</span>
          </div>

          <div className="flex items-center gap-1">
            <span className="font-semibold">Fast Delivery</span>
          </div>

          <div className="h-4 w-px bg-blue-300"></div>

          <div className="flex items-center gap-1">
            <span className="font-semibold">Real-time Tracking</span>
          </div>

          <div className="h-4 w-px bg-blue-300"></div>

          <div className="flex items-center gap-1">
            <span className="font-semibold">Best Rates</span>
          </div>

          <div className="h-4 w-px bg-blue-300"></div>

          <p className="text-blue-100">
            Welcome to Sureship — Your trusted logistics partner for seamless shipping solutions
          </p>

          <div className="h-4 w-px bg-blue-300"></div>



        </div>
      </div>

      <nav
        className={`
          fixed z-40 transition-all duration-200
          ${scrolled
            ? "top-0 lg:top-0 left-1/2 -translate-x-1/2 w-[90%] h-[68px] bg-blue-100 shadow-md rounded-3xl"
            : "top-0 lg:top-10 left-1/2 -translate-x-1/2 bg-blue-100 w-full h-[68px]"
          }
        `}
      >
        <div className="max-w-7xl mx-auto flex items-center justify-between h-16 px-4 lg:px-6">
          {/* Logo */}
          <div className="flex items-center flex-shrink-0">
            <Link href="/" onClick={closeMobileMenu}>
              <Image
                src="/image/e (1).png"
                alt="Sureship Logo"
                width={2400}
                height={1600}
                className="object-contain transition-all duration-300 w-[180px] md:w-[230px] h-auto"
              />
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-4 lg:gap-6 flex-1 justify-center">
            <Link
              href="/"
              className="text-gray-700 hover:text-blue-600 text-sm lg:text-base transition-all hover:scale-105 relative group"
            >
              Home
              <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-blue-600 transition-all group-hover:w-full"></span>
            </Link>

            {/* Services Dropdown */}
            <div className="relative" ref={servicesRef}>
              <button
                onClick={() => setServicesOpen(!servicesOpen)}
                className="flex items-center gap-1 text-gray-700 hover:text-blue-600 text-sm lg:text-base whitespace-nowrap cursor-pointer transition-all hover:scale-105 relative group"
              >
                Services
                <svg
                  className={`w-4 h-4 transform transition-transform duration-300 ${servicesOpen ? "rotate-180" : "rotate-0"}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-blue-600 transition-all group-hover:w-full"></span>
              </button>

              {/* Dropdown */}
              <div
                className={`absolute left-0 mt-2 w-56 bg-white rounded-lg shadow-xl z-50 border border-gray-200
                  transition-all duration-200 origin-top
                  ${servicesOpen
                    ? "opacity-100 scale-100 visible pointer-events-auto"
                    : "opacity-0 scale-95 invisible pointer-events-none"
                  }
                `}
              >
                <ul className="py-2">
                  <li>
                    <Link
                      href="/Services/Express-parcel"
                      className="flex items-center gap-3 px-4 py-2.5 hover:bg-blue-50 text-sm lg:text-base text-gray-700 transition-all group"
                      onClick={() => setServicesOpen(false)}
                    >
                      <FaTruck className="text-blue-600 group-hover:text-blue-700 transition-colors" />
                      <span>Express Parcel</span>
                    </Link>
                  </li>
                  <li>
                    <Link
                      href="/Services/warehousing"
                      className="flex items-center gap-3 px-4 py-2.5 hover:bg-blue-50 text-sm lg:text-base text-gray-700 transition-all group"
                      onClick={() => setServicesOpen(false)}
                    >
                      <FaWarehouse className="text-blue-600 group-hover:text-blue-700 transition-colors" />
                      <span>Warehousing</span>
                    </Link>
                  </li>
                  <li>
                    <Link
                      href="/Services/Truckload"
                      className="flex items-center gap-3 px-4 py-2.5 hover:bg-blue-50 text-sm lg:text-base text-gray-700 transition-all group"
                      onClick={() => setServicesOpen(false)}
                    >
                      <FaRoute className="text-blue-600 group-hover:text-blue-700 transition-colors" />
                      <span>Part Truckload</span>
                    </Link>
                  </li>
                  <li>
                    <Link
                      href="/Services/Crossborder"
                      className="flex items-center gap-3 px-4 py-2.5 hover:bg-blue-50 text-sm lg:text-base text-gray-700 transition-all group"
                      onClick={() => setServicesOpen(false)}
                    >
                      <FaGlobe className="text-blue-600 group-hover:text-blue-700 transition-colors" />
                      <span>Cross Border</span>
                    </Link>
                  </li>
                  {/* <li className="border-t border-gray-200 mt-1 pt-1">
                    <a
                      href="https://documenter.getpostman.com/view/33711269/2sB3HgPNbj"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-3 px-4 py-2.5 hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 text-sm lg:text-base text-blue-700 transition-all group"
                      onClick={() => setServicesOpen(false)}
                    >
                      <FaCode className="text-blue-600 group-hover:text-blue-700 transition-colors" />
                      <span>API Documentation</span>
                      <FiExternalLink className="ml-auto text-xs opacity-70 group-hover:opacity-100 transition-opacity" />
                    </a>
                  </li> */}
                </ul>
              </div>
            </div>

            {/* Company Dropdown */}
            <div className="relative" ref={companyRef}>
              <button
                onClick={() => setCompanyOpen(!companyOpen)}
                className="flex items-center gap-1 text-gray-700 hover:text-blue-600 text-sm lg:text-base whitespace-nowrap cursor-pointer transition-all hover:scale-105 relative group"
              >
                Company
                <svg
                  className={`w-4 h-4 transform transition-transform duration-300 ${companyOpen ? "rotate-180" : "rotate-0"}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-blue-600 transition-all group-hover:w-full"></span>
              </button>

              {/* Dropdown */}
              <div
                className={`absolute left-0 mt-2 w-56 bg-white rounded-lg shadow-xl z-50 border border-gray-200
                  transition-all duration-200 origin-top
                  ${companyOpen ? "opacity-100 scale-100 visible pointer-events-auto" : "opacity-0 scale-95 invisible pointer-events-none"}
                `}
              >
                <ul className="py-2">
                  <li>
                    <Link
                      href="/aboutus"
                      className="flex items-center gap-3 px-4 py-2.5 hover:bg-blue-50 text-sm lg:text-base text-gray-700 transition-all group"
                      onClick={() => setCompanyOpen(false)}
                    >
                      <FaInfoCircle className="text-blue-600 group-hover:text-blue-700 transition-colors" />
                      <span>About us</span>
                    </Link>
                  </li>
                  <li>
                    <Link
                      href="/careers"
                      className="flex items-center gap-3 px-4 py-2.5 hover:bg-blue-50 text-sm lg:text-base text-gray-700 transition-all group"
                      onClick={() => setCompanyOpen(false)}
                    >
                      <FaBriefcase className="text-blue-600 group-hover:text-blue-700 transition-colors" />
                      <span>Careers</span>
                    </Link>
                  </li>
                  <li>
                    <Link
                      href="/Explore"
                      className="flex items-center gap-3 px-4 py-2.5 hover:bg-blue-50 text-sm lg:text-base text-gray-700 transition-all group"
                      onClick={() => setCompanyOpen(false)}
                    >
                      <FaCompass className="text-blue-600 group-hover:text-blue-700 transition-colors" />
                      <span>Explore us</span>
                    </Link>
                  </li>
                </ul>
              </div>
            </div>

            {/* <Link
              href="/track"
              className="text-gray-700 hover:text-blue-600 text-sm lg:text-base transition-all hover:scale-105 relative group"
            >
              Track
              <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-blue-600 transition-all group-hover:w-full"></span>
            </Link> */}

            <Link
              href="/contactus"
              className="text-gray-700 whitespace-nowrap hover:text-blue-600 text-sm lg:text-base transition-all hover:scale-105 relative group"
            >
              Contact us
              <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-blue-600 transition-all group-hover:w-full"></span>
            </Link>
          </div>

          {/* Desktop Action Buttons */}
          <div className="hidden md:flex items-center gap-3 lg:gap-4 flex-shrink-0">
            <a
              href="https://app.sureship.in"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-blue-900 text-white px-4 lg:px-5 py-2 rounded-md text-sm flex items-center gap-2 hover:bg-blue-800 transition-colors"
            >
              Login
            </a>
            <Link
              href="/RateCalculator"
              className="bg-blue-900 text-white px-4 lg:px-5 py-2 rounded-md whitespace-nowrap text-sm flex items-center gap-2 hover:bg-blue-800 transition-colors"
            >
              Rate Calculator
            </Link>

            <Link
              href="/track"
              className="bg-blue-900 text-white px-4 lg:px-5 py-2 rounded-md whitespace-nowrap text-sm flex items-center gap-2 hover:bg-blue-800 transition-colors"
            >
              Track
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden flex items-center">
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              className="text-3xl text-blue-700 hover:text-blue-800 transition-colors p-2"
              aria-label="Toggle menu"
            >
              {menuOpen ? <HiX /> : <HiMenu />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {menuOpen && (
          <div
            ref={mobileMenuRef}
            className="md:hidden px-6 py-4 space-y-3 bg-white shadow-lg border-t border-gray-200"
          >
            <button
              type="button"
              onClick={() => handleMobileNavClick("/")}
              className="block w-full text-left text-gray-700 hover:text-blue-600 py-2 transition-colors"
            >
              Home
            </button>

            {/* Services Dropdown */}
            <div ref={mobileServicesRef}>
              <button
                onClick={() => setServicesOpen(!servicesOpen)}
                className="w-full flex items-center justify-between text-gray-700 py-2 hover:text-blue-600 transition-colors"
              >
                <span>Services</span>
                <svg
                  className={`w-4 h-4 transform transition-transform duration-200 ${servicesOpen ? "rotate-180" : "rotate-0"}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>

              {/* Dropdown menu */}
              {servicesOpen && (
                <ul className="mt-1 space-y-1 pl-4 border-l-2 border-blue-200">
                  <li>
                    <button
                      type="button"
                      onClick={() => handleMobileNavClick("/Services/Express-parcel")}
                      className="flex w-full text-left items-center gap-3 px-3 py-2.5 text-gray-700 hover:text-blue-600 hover:bg-blue-50 rounded transition-all group"
                    >
                      <FaTruck className="text-blue-600 text-sm" />
                      <span>Express Parcel</span>
                    </button>
                  </li>
                  <li>
                    <button
                      type="button"
                      onClick={() => handleMobileNavClick("/Services/warehousing")}
                      className="flex w-full text-left items-center gap-3 px-3 py-2.5 text-gray-700 hover:text-blue-600 hover:bg-blue-50 rounded transition-all group"
                    >
                      <FaWarehouse className="text-blue-600 text-sm" />
                      <span>Warehousing</span>
                    </button>
                  </li>
                  <li>
                    <button
                      type="button"
                      onClick={() => handleMobileNavClick("/Services/Truckload")}
                      className="flex w-full text-left items-center gap-3 px-3 py-2.5 text-gray-700 hover:text-blue-600 hover:bg-blue-50 rounded transition-all group"
                    >
                      <FaRoute className="text-blue-600 text-sm" />
                      <span>Part Truckload</span>
                    </button>
                  </li>
                  <li>
                    <button
                      type="button"
                      onClick={() => handleMobileNavClick("/Services/Crossborder")}
                      className="flex w-full text-left items-center gap-3 px-3 py-2.5 text-gray-700 hover:text-blue-600 hover:bg-blue-50 rounded transition-all group"
                    >
                      <FaGlobe className="text-blue-600 text-sm" />
                      <span>Cross Border</span>
                    </button>
                  </li>
                  {/* <li className="border-t border-blue-200 mt-2 pt-2">
                    <a
                      href="https://documenter.getpostman.com/view/33711269/2sB3HgPNbj"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-3 px-3 py-2.5 text-blue-700 hover:text-blue-800 hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 rounded transition-all group"
                      onClick={closeMobileMenu}
                    >
                      <FaCode className="text-blue-600 text-sm" />
                      <span>API Documentation</span>
                      <FiExternalLink className="ml-auto text-xs" />
                    </a>
                  </li> */}
                </ul>
              )}
            </div>

            {/* Company Dropdown */}
            <div ref={mobileCompanyRef}>
              <button
                onClick={() => setCompanyOpen(!companyOpen)}
                className="w-full flex items-center justify-between text-gray-700 py-2 hover:text-blue-600 transition-colors"
              >
                <span>Company</span>
                <svg
                  className={`w-4 h-4 transform transition-transform duration-200 ${companyOpen ? "rotate-180" : "rotate-0"}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>

              {companyOpen && (
                <ul className="mt-1 space-y-1 pl-4 border-l-2 border-blue-200">
                  <li>
                    <button
                      type="button"
                      onClick={() => handleMobileNavClick("/aboutus")}
                      className="flex w-full text-left items-center gap-3 px-3 py-2.5 text-gray-700 hover:text-blue-600 hover:bg-blue-50 rounded transition-all group"
                    >
                      <FaInfoCircle className="text-blue-600 text-sm" />
                      <span>About us</span>
                    </button>
                  </li>
                  <li>
                    <button
                      type="button"
                      onClick={() => handleMobileNavClick("/careers")}
                      className="flex w-full text-left items-center gap-3 px-3 py-2.5 text-gray-700 hover:text-blue-600 hover:bg-blue-50 rounded transition-all group"
                    >
                      <FaBriefcase className="text-blue-600 text-sm" />
                      <span>Careers</span>
                    </button>
                  </li>
                  <li>
                    <button
                      type="button"
                      onClick={() => handleMobileNavClick("/Explore")}
                      className="flex w-full text-left items-center gap-3 px-3 py-2.5 text-gray-700 hover:text-blue-600 hover:bg-blue-50 rounded transition-all group"
                    >
                      <FaCompass className="text-blue-600 text-sm" />
                      <span>Explore us</span>
                    </button>
                  </li>
                </ul>
              )}
            </div>

            {/* <Link
              href="/track"
              className="block text-gray-700 hover:text-blue-600 py-2 transition-colors"
              onClick={closeMobileMenu}
            >
              Track
            </Link> */}

            <button
              type="button"
              onClick={() => handleMobileNavClick("/contactus")}
              className="block w-full text-left text-gray-700 hover:text-blue-600 py-2 transition-colors"
            >
              Contact us
            </button>

            {/* Mobile Action Buttons */}
            <div className="pt-4 space-y-3 border-t border-gray-200 flex flex-col items-start">

              <a
                href="https://app.sureship.in"
                target="_blank"
                rel="noopener noreferrer"
                onClick={closeMobileMenu}
                className="flex w-52 h-12 bg-blue-900 text-white rounded-lg text-sm font-medium items-center justify-center hover:bg-blue-800"
              >
                Login
              </a>

              <button
                type="button"
                onClick={() => handleMobileNavClick("/RateCalculator")}
                className="flex w-52 h-12 bg-blue-900 text-white rounded-lg text-sm font-medium items-center justify-center hover:bg-blue-800"
              >
                Rate Calculator
              </button>

              <button
                type="button"
                onClick={() => handleMobileNavClick("/track")}
                className="flex w-52 h-12 bg-blue-900 text-white rounded-lg text-sm font-medium items-center justify-center hover:bg-blue-800"
              >
                Track
              </button>

            </div>

          </div>
        )}
      </nav>
    </div>
  );
}
